import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Button, Card } from "react-bootstrap";
import Form from "react-bootstrap/Form";
import { Controller, useForm } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import Select from "react-select";
import styled from "styled-components";
import { withAuthorizationVerifier } from "../../components/authorization-verifier";
import ContentWrapper from "../../components/content-wrapper";
import { useProperty } from "../../components/property-provider";
import { SectionHeading } from "../../components/section-heading";
import { useConfig } from "../../lib/config";
import { useHttpClient } from "../../lib/http";
import { StyledSpinner } from "../ariSnapshot/styles";
import { regenPropertyCache } from "../common/api";
import { OccupancyEntity, OccupationType } from "./domain/Occupancy.entity";
import { useOccupancyApi } from "./infrastructure/occupancy.api";
import { occupationTypesMessages } from "./messages";
import { OccupancyFormData, occupancyFormSchema } from "./schema";

const CHILDREN_TYPES = [
  OccupationType.FOR_ADULTS_CHILDREN,
  OccupationType.FOR_ADULTS_CHILDREN_BABIES,
  OccupationType.FOR_ADULTS_TEENS_CHILDREN,
  OccupationType.FOR_ADULTS_TEENS_CHILDREN_BABIES,
  OccupationType.FOR_SENIORS_ADULTS_TEENS_CHILDREN_BABIES,
];

const BABIES_TYPES = [
  OccupationType.FOR_ADULTS_CHILDREN_BABIES,
  OccupationType.FOR_ADULTS_TEENS_CHILDREN_BABIES,
  OccupationType.FOR_SENIORS_ADULTS_TEENS_CHILDREN_BABIES,
];

const TEENS_TYPES = [
  OccupationType.FOR_ADULTS_TEENS_CHILDREN,
  OccupationType.FOR_ADULTS_TEENS_CHILDREN_BABIES,
  OccupationType.FOR_SENIORS_ADULTS_TEENS_CHILDREN_BABIES,
];

const SENIORS_TYPES = [OccupationType.FOR_SENIORS_ADULTS_TEENS_CHILDREN_BABIES];

const ButtonContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

function OccupancyForm() {
  const [showAlert, setShowAlert] = useState(false);
  const [loading, setLoading] = useState(false);
  const [alertType, setAlertType] = useState("success");

  const intl = useIntl();
  const propertyInfo = useProperty();
  const configuration = useConfig();
  const middlewareClient = useHttpClient(configuration.middlewareEndpoint);
  const apiClient = useHttpClient(configuration.secureApiEndpoint);
  const { getOccupancyOptions, updateOccupancyOptions } = useOccupancyApi(middlewareClient);
  const {
    register,
    watch,
    handleSubmit,
    control,
    reset,
    formState: { errors },
    setValue,
  } = useForm<OccupancyFormData>({
    resolver: zodResolver(occupancyFormSchema(intl)),
  });

  const occupationOptions: { value: string; label: string }[] = useMemo(
    () => [
      {
        value: OccupationType.NO_FILTER,
        label: intl.formatMessage(occupationTypesMessages.optionTypeWithoutFilter),
      },
      {
        value: OccupationType.PER_PERSON,
        label: intl.formatMessage(occupationTypesMessages.optionTypePerPerson),
      },
      {
        value: OccupationType.FOR_ADULTS_CHILDREN,
        label: intl.formatMessage(occupationTypesMessages.optionTypeForAdultsChildren),
      },
      {
        value: OccupationType.FOR_ADULTS_CHILDREN_BABIES,
        label: intl.formatMessage(occupationTypesMessages.optionTypeForAdultsChildrenBabies),
      },
      {
        value: OccupationType.FOR_ADULTS_TEENS_CHILDREN,
        label: intl.formatMessage(occupationTypesMessages.optionTypeForAdultsTeensChildren),
      },
      {
        value: OccupationType.FOR_ADULTS_TEENS_CHILDREN_BABIES,
        label: intl.formatMessage(occupationTypesMessages.optionTypeForAdultsTeensChildrenBabies),
      },
      {
        value: OccupationType.FOR_SENIORS_ADULTS_TEENS_CHILDREN_BABIES,
        label: intl.formatMessage(
          occupationTypesMessages.optionTypeForSeniorsAdultsTeensChildrenBabies
        ),
      },
    ],
    [intl]
  );

  const onSubmit = useCallback(
    async (data: OccupancyFormData) => {
      const occupancy = OccupancyEntity.of({
        occupationType: data.occupationType.value as OccupationType,
        minChildrenAge: data.minChildrenAge,
        maxChildrenAge: data.maxChildrenAge,
        minBabiesAge: data.minBabiesAge,
        maxTeensAge: data.maxTeensAge,
        maxSeniorsAge: data.maxSeniorsAge,
        filterByAge: data.filterByAge,
      });

      try {
        setLoading(true);
        await updateOccupancyOptions(propertyInfo.ticker, occupancy);
        await regenPropertyCache(apiClient, propertyInfo.ticker);
        setAlertType("success");
      } catch (e) {
        setAlertType("danger");
      } finally {
        setShowAlert(true);
        setLoading(false);
        setTimeout(() => setShowAlert(false), 3000);
      }
    },
    [apiClient, propertyInfo.ticker, updateOccupancyOptions]
  );

  const fetchOccupancy = useCallback(async () => {
    const occupancy = await getOccupancyOptions(propertyInfo.ticker);
    const formData = {
      ...occupancy,
      occupationType: occupationOptions.find(o => o.value === occupancy.occupationType),
    };
    reset(formData);
  }, [getOccupancyOptions, propertyInfo.ticker, occupationOptions, reset]);

  useEffect(() => {
    fetchOccupancy();
  }, [fetchOccupancy]);

  const occupationType =
    (watch("occupationType")?.value as OccupationType) ?? OccupationType.NO_FILTER;

  useEffect(() => {
    if (!CHILDREN_TYPES.includes(occupationType)) {
      setValue("minChildrenAge", null);
      setValue("maxChildrenAge", null);
    } else {
      setValue("minChildrenAge", watch("minChildrenAge") ?? 0);
      setValue("maxChildrenAge", watch("maxChildrenAge") ?? 0);
    }

    if (!BABIES_TYPES.includes(occupationType)) {
      setValue("minBabiesAge", null);
    } else {
      setValue("minBabiesAge", watch("minBabiesAge") ?? 0);
    }

    if (!TEENS_TYPES.includes(occupationType)) {
      setValue("maxTeensAge", null);
    } else {
      setValue("maxTeensAge", watch("maxTeensAge") ?? 0);
    }

    if (!SENIORS_TYPES.includes(occupationType)) {
      setValue("maxSeniorsAge", null);
    } else {
      setValue("maxSeniorsAge", watch("maxSeniorsAge") ?? 0);
    }
  }, [occupationType, setValue, watch]);

  return (
    <ContentWrapper>
      <SectionHeading>
        <FormattedMessage {...occupationTypesMessages.title} />
      </SectionHeading>
      <Card className="ml-4 mr-4 mb-4 mt-4">
        <Card.Body>
          <Form onSubmit={handleSubmit(onSubmit)} className="w-[320px]">
            <Form.Group>
              <Form.Label htmlFor="occupationType">
                <FormattedMessage {...occupationTypesMessages.occupancyType} />
              </Form.Label>
              <Controller
                name="occupationType"
                control={control}
                render={({ field }) => <Select {...field} options={occupationOptions} />}
              />
            </Form.Group>

            {CHILDREN_TYPES.includes(occupationType) && (
              <>
                <Form.Group>
                  <Form.Label htmlFor="minChildrenAge">
                    <FormattedMessage {...occupationTypesMessages.minChildrenAge} />
                  </Form.Label>
                  <Form.Control
                    id="minChildrenAge"
                    {...register("minChildrenAge")}
                    type="number"
                    isInvalid={errors.minChildrenAge != null}
                  />
                </Form.Group>
                <Form.Group>
                  <Form.Label htmlFor="maxChildrenAge">
                    <FormattedMessage {...occupationTypesMessages.maxChildrenAge} />
                  </Form.Label>
                  <Form.Control
                    id="maxChildrenAge"
                    {...register("maxChildrenAge")}
                    type="number"
                    isInvalid={errors.maxChildrenAge != null}
                  />
                </Form.Group>
              </>
            )}
            {errors.maxChildrenAge && (
              <span className="text-red-500">{errors.maxChildrenAge.message}</span>
            )}
            {errors.minChildrenAge && (
              <span className="text-red-500">{errors.minChildrenAge.message}</span>
            )}

            {BABIES_TYPES.includes(occupationType) && (
              <Form.Group>
                <Form.Label htmlFor="minBabiesAge">
                  <FormattedMessage {...occupationTypesMessages.minBabiesAge} />
                </Form.Label>
                <Form.Control
                  id="minBabiesAge"
                  {...register("minBabiesAge")}
                  type="number"
                  isInvalid={errors.minBabiesAge != null}
                />
              </Form.Group>
            )}
            {errors.minBabiesAge && (
              <span className="text-red-500">{errors.minBabiesAge.message}</span>
            )}

            {TEENS_TYPES.includes(occupationType) && (
              <Form.Group>
                <Form.Label htmlFor="maxTeensAge">
                  <FormattedMessage {...occupationTypesMessages.maxTeensAge} />
                </Form.Label>
                <Form.Control
                  id="maxTeensAge"
                  {...register("maxTeensAge")}
                  type="number"
                  isInvalid={errors.maxTeensAge != null}
                />
              </Form.Group>
            )}
            {errors.maxTeensAge && (
              <span className="text-red-500">{errors.maxTeensAge.message}</span>
            )}

            {SENIORS_TYPES.includes(occupationType) && (
              <Form.Group>
                <Form.Label htmlFor="maxSeniorsAge">
                  <FormattedMessage {...occupationTypesMessages.maxAdultsAge} />
                </Form.Label>
                <Form.Control
                  id="maxSeniorsAge"
                  {...register("maxSeniorsAge")}
                  type="number"
                  isInvalid={errors.maxSeniorsAge != null}
                />
              </Form.Group>
            )}
            {errors.maxSeniorsAge && (
              <span className="text-red-500">{errors.maxSeniorsAge.message}</span>
            )}

            <Form.Group>
              <Form.Check
                label={intl.formatMessage(occupationTypesMessages.filterGuestsByAge)}
                {...register("filterByAge")}
              />
            </Form.Group>

            <Button type="submit" disabled={loading}>
              <ButtonContainer>
                <FormattedMessage {...occupationTypesMessages.save} />
                {loading && <StyledSpinner className="fa fa-spinner fa-spin" />}
              </ButtonContainer>
            </Button>
          </Form>

          <Alert hidden={!showAlert} variant={alertType} className="w-fit mt-3 mb-0 self-center">
            {alertType === "success" ? (
              <FormattedMessage {...occupationTypesMessages.saveSuccess} />
            ) : (
              <FormattedMessage {...occupationTypesMessages.saveError} />
            )}
          </Alert>
        </Card.Body>
      </Card>
    </ContentWrapper>
  );
}

export default withAuthorizationVerifier(OccupancyForm);
